import {
  alpha,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LabelExportFormat, LabelFormat } from "generated-graphql/graphql";
import { ReactElement } from "react";

/** Shared "Print format" and "File type" controls for the container label
 * screen's Print & format card and the container list's bulk export dialog
 * -- the two surfaces render the same rich radio option cards and toggle
 * group so the two flows look and behave the same. */

function FormatOptionCard({
  value,
  title,
  description,
  selected,
  row,
}: {
  value: LabelFormat;
  title: string;
  description: string;
  selected: boolean;
  row: boolean;
}): ReactElement {
  return (
    <Paper
      variant="outlined"
      data-testid={`format-option-${value}`}
      data-selected={selected}
      sx={{
        p: 1.5,
        mb: row ? 0 : 1,
        flex: row ? 1 : undefined,
        minWidth: 0,
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? "secondary.main" : "divider",
        backgroundColor: (theme) =>
          selected ? alpha(theme.palette.secondary.main, 0.08) : "transparent",
      }}
    >
      <FormControlLabel
        value={value}
        control={<Radio color="secondary" />}
        sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
        label={
          <Stack sx={{ pt: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Stack>
        }
      />
    </Paper>
  );
}

/** Looks up a `RadioGroup` change event's plain string value against the
 * known layouts, so the handler never needs to cast a string back to
 * `LabelFormat`. */
const LABEL_FORMAT_BY_VALUE: Record<string, LabelFormat> = {
  [LabelFormat.Full]: LabelFormat.Full,
  [LabelFormat.QrOnly]: LabelFormat.QrOnly,
};

export function FormatRadioGroup({
  layout,
  onChange,
  /** Side by side, for surfaces with width to spare. The bulk dialog is
   * narrow and keeps the stacked default. */
  row = false,
}: {
  layout: LabelFormat;
  onChange: (layout: LabelFormat) => void;
  row?: boolean;
}): ReactElement {
  return (
    <RadioGroup
      aria-label="Print format"
      row={row}
      value={layout}
      onChange={(event) => {
        const selected = LABEL_FORMAT_BY_VALUE[event.target.value];
        if (selected) {
          onChange(selected);
        }
      }}
      sx={row ? { flexWrap: "nowrap", gap: 2 } : undefined}
    >
      <FormatOptionCard
        value={LabelFormat.Full}
        title="Full label"
        description="Compliant HW label with DOT marking"
        selected={layout === LabelFormat.Full}
        row={row}
      />
      <FormatOptionCard
        value={LabelFormat.QrOnly}
        title="QR code only"
        description="For customers who use their own labels"
        selected={layout === LabelFormat.QrOnly}
        row={row}
      />
    </RadioGroup>
  );
}

export function FileTypeToggle({
  value,
  onChange,
  disablePng,
}: {
  value: LabelExportFormat;
  onChange: (value: LabelExportFormat) => void;
  disablePng?: boolean;
}): ReactElement {
  return (
    // color="primary" gives the selected button the theme-primary (Sapphire)
    // tinted background the spec calls for -- unlike the secondary/orange
    // treatment sibling CTAs on this screen use, this control's selected
    // state mirrors the design's blue fill.
    <ToggleButtonGroup
      color="primary"
      exclusive
      size="small"
      value={value}
      onChange={(_event, next: LabelExportFormat | null) => {
        if (next) {
          onChange(next);
        }
      }}
      sx={{ display: "flex" }}
    >
      <ToggleButton value={LabelExportFormat.Pdf} sx={{ flex: 1 }}>
        PDF
      </ToggleButton>
      <ToggleButton value={LabelExportFormat.Png} sx={{ flex: 1 }} disabled={disablePng}>
        PNG
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
